import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { isNullOrUndefined } from 'util';
import { config } from '../../../config/config';
import {
    VisitorCalendarActionCommand,
    VisitorSettingsCommand,
    VisitorStyleCommand,
} from '../../commands/pagination.query.command';
import { AllFrontendSettingsCommand } from '../../commands/visitor/all.frontend.settings.command';
import { BaseComponent, BaseComponentOption } from '../../common/components/base.component';
import { PageSpinnerComponent } from '../../common/components/page.spinner/page.spinner.component';
import { localStorageKeys } from '../../common/constants/local.storage.keys';
import { getRouteUrl, routeConfigs } from '../../common/constants/route.config';
import { HubVisitorFeatureToggle } from '../../models/hub/hub.visitor.feature.toggle';
import { HubVisitorPromotionImage } from '../../models/hub/hub.visitor.promotion.image';
import { CustomizationApiService } from '../../services/customization.api.service';
import { ImageService } from '../../services/image.service';
import {
    AllFrontendSettingsStore,
    VisitorCalendarStore,
    VisitorDataCache,
    VisitorStyleStore,
} from '../../stores/visitor.calendar.store';
import { VisitorCalendarComponent } from './calendar/visitor.calendar.component';
import { VisitorCreateEventComponent } from './create.event/create.event.component';
import { VisitorCreatePlaceComponent } from './create.place/create.place.component';
import { VisitorTripComponent } from './trip/visitor.trip.component';
import { ModalService } from '../../common/services/modal.service';
import { VisitorPlaceMapComponent } from './place.map/visitor.place.map.component';
import { MediaQueryService } from '../../services/media.query.service';

@Component({
    styleUrls: ['./visitor.component.scss'],
    templateUrl: './visitor.component.html',
})
export class VisitorComponent extends BaseComponent implements OnInit {
    public isSideBarOpened = false;
    public hubName = null;
    public isShowUpgradeBrowser = true;
    // public customerSettings = new VisitorSettingsCommand();
    public featuresToggle = new HubVisitorFeatureToggle();
    public allFrontendSettings = new AllFrontendSettingsCommand();
    public isSettingsLoaded = false;

    public visitorPromotionImage = new HubVisitorPromotionImage();
    public defaultVisitorPromotionImg = config.defaultVisitorPromotionImageUrl;
    @ViewChild('outlet') private _outlet: RouterOutlet;
    @ViewChild(PageSpinnerComponent) public spinner: PageSpinnerComponent;

    constructor(
        private customizationApiService: CustomizationApiService,
        private visitorStyleStore: VisitorStyleStore,
        private imageService: ImageService,
        private router: Router,
        private modalService: ModalService,
        private allFrontendSettingsStore: AllFrontendSettingsStore,
        private visitorCalendarStore: VisitorCalendarStore,
        private activatedRoute: ActivatedRoute
    ) {
        super({ activatedRoute });
    }

    public ngOnInit() {
        let { hubName } = this.allRouteParams;
        this.hubName = hubName;
        this.spinner.show();

        try {
            this.customizationApiService
                .getAllFrontendSettings(this.hubName)
                .subscribe((allSettings: AllFrontendSettingsCommand) => {
                    if (!allSettings) {
                        // alert(`Account ${this.hubName} not found, please verify!`);
                        console.error(`Account ${this.hubName} not found, please verify!`);
                        return;
                    }
                    // this storage records the selected place category, which is used to bring the selected category to map view
                    localStorage.removeItem(localStorageKeys.selectedPlaceCategory);

                    this.allFrontendSettings = allSettings;
                    let settings = allSettings.customSettings;
                    localStorage.setItem(
                        localStorageKeys.timezoneFormatterDefault,
                        allSettings.customSettings.timezoneFormatter
                    );
                    this.setCustomerColor(settings.Style);

                    let logoUrl =
                        isNullOrUndefined(settings.logo) ||
                        isNullOrUndefined(settings.logo.isDefault) ||
                        settings.logo.isDefault
                            ? config.defaultLogoUrl
                            : settings.logo.url;
                    VisitorDataCache.logo = this.imageService.imageSourceUrl(logoUrl);
                    VisitorDataCache.placeCategories = allSettings.placeCategories;
                    this.allFrontendSettingsStore.allFrontendSettingsCommand.next(this.allFrontendSettings);
                    // for event details modal
                    localStorage.setItem(
                        localStorageKeys.visitorSettings,
                        JSON.stringify(this.allFrontendSettings.customSettings)
                    );
                    this.isSettingsLoaded = true;
                });
        } catch (e) {
            alert(e);
        } finally {
            setTimeout(() => {
                this.spinner.hide();
            });
        }
    }

    public get isNeedSideBar() {
        try {
            /**
             * VisitorPlaceMapComponent needs to have topbar (in topbar mode) is because the multi-level nested dropdown menu would have UX issue
             * after view changed. So keep the topbar alive but hide it when switching to place map view
             * */
            return (
                this._outlet.activatedRoute.component === VisitorCalendarComponent ||
                //this._outlet.activatedRoute.component === VisitorEventMapComponent ||
                (!this.allFrontendSettings.customSettings.menuStyle.isSideBar &&
                    this._outlet.activatedRoute.component === VisitorPlaceMapComponent) ||
                this._outlet.activatedRoute.component === VisitorCreateEventComponent ||
                this._outlet.activatedRoute.component === VisitorCreatePlaceComponent
            );
        } catch (e) {
            return true;
        }

        // don't know why using the Component Name VisitorTripComponent causes a script error
        //this._outlet.activatedRoute.component.toString().indexOf('VisitorTripComponent') !== -1 ;
    }

    /**
     * VisitorPlaceMapComponent needs to have topbar (in topbar mode) is because the multi-level nested dropdown menu would have UX issue
     * after view changed. So keep the topbar alive but hide it when switching to place map view
     * */
    public get isHideTopBar() {
        if (!this.isSettingsLoaded || !this._outlet) return false;
        return (
            this._outlet.activatedRoute.component === VisitorPlaceMapComponent &&
            !this.allFrontendSettings.customSettings.menuStyle.isSideBar
        );
    }

    public get isNoTopMenuForMobile() {
        return this._outlet.activatedRoute.component.toString().indexOf('VisitorTripComponent') !== -1;
    }

    public upgradeBrowser() {
        window.open(
            'https://browser-update.org/update-browser.html?force_outdated=true#3.3.15:browser-update.org',
            '_blank'
        );
    }

    public hideUpgradeBrowser() {
        this.isShowUpgradeBrowser = false;
    }

    // /**
    //  * solve the browser's back button is triggered, event calendar shows empty content issue
    //  * now prevent default in event.details.modal.component.ts
    //  * */
    // @HostListener('window:popstate', ['$event'])
    // public hashChanged($event) {
    //     if (this.isScriptSolution) {
    //         setTimeout(() => {
    //             const storage = localStorage.getItem(localStorageKeys.eventQueryCommand);
    //             let actionCommand = storage ? JSON.parse(storage) : new VisitorCalendarActionCommand();
    //             console.log('command from storage?', storage);
    //             console.log('command load', actionCommand);

    //             this.visitorCalendarStore.actionCommand.next(actionCommand);
    //             this.visitorStyleStore.styleCommand.next(new VisitorStyleCommand());
    //             console.log('command sent');

    //             this.router.navigateByUrl(
    //                 getRouteUrl(routeConfigs.visitor.eventCalendar, {
    //                     hubName: this.hubName,
    //                 }),
    //                 { skipLocationChange: this.noChangeUrlWhenRedirect }
    //             );
    //         });
    //     }
    // }

    private setCustomerColor(data) {
        if (!data) {
            return;
        }
        let head = document.getElementsByTagName('head')[0];
        let s = document.createElement('style');
        s.setAttribute('type', 'text/css');
        let css = '';
        if (data.defaultButton.backgroundColor) {
            css += `--default-btn-background-color: ${data.defaultButton.backgroundColor};`;
        }
        if (data.eventButton.backgroundColor) {
            css += `--event-btn-background-color: ${data.eventButton.backgroundColor};`;
            // let rgb = hexToRgb(data.eventButton.backgroundColor);
            // console.log(`isLightColor(rgb): ${isLightColor(rgb)}`);
        }
        if (data.placeButton.backgroundColor) {
            css += `--place-btn-background-color: ${data.placeButton.backgroundColor};`;
        }
        if (data.pinnedEventBorder.borderColor) {
            css += `--pinned-event-border-color: ${data.pinnedEventBorder.borderColor};`;
        }
        if (data.detailButton.backgroundColor) {
            css += `--detail-btn-background-color: ${data.detailButton.backgroundColor};`;
        }
        if (data.sidebarUnderline.color) {
            css += `--sidebar-underline-color: ${data.sidebarUnderline.color};`;
        }
        s.appendChild(
            document.createTextNode(`
                :root {
                  ${css}
                }
            `)
        );
        head.appendChild(s);
    }
}



// WEBPACK FOOTER //
// D:/Work/Development/ImGoing/IMGoing/web/src/components/visitor/visitor.component.ts